import * as React from 'react'
import { ContextFrom, EventFrom, InvokeCreator } from 'xstate'

import { TLanguage } from '~config/locales'

import { getBaseHttpHeaders } from '../../graphQL/httpHeaders'
import { authMachine } from '../service/authMachine'
import { fetchTokenForGuest, fetchTokenWithPassword } from './fetchCustomerToken'

type TContext = ContextFrom<typeof authMachine>
type TEvent = EventFrom<typeof authMachine>
type TAuthMachineService = InvokeCreator<TContext, TEvent>

type TOptions = {
  backendUrl: string
  locale: TLanguage['code']
}

/**
 * Returns services that the `authMachine` needs to fetch guest or user token.
 * The services are being passed to the `useInterpret` hook when the machine is started.
 */
export const useFetchTokenServices = ({ backendUrl, locale }: TOptions) => {
  const headers = getBaseHttpHeaders({ locale, authToken: null })

  const obtainUserToken = React.useCallback<TAuthMachineService>(
    (_, event) => {
      if (event.type === 'LOGIN') {
        const { password, username } = event.payload
        return fetchTokenWithPassword({ backendUrl, password, username, headers })
      }

      // we have to return a Promise so the machine can handle it correctly
      // rejected Promise will lead to the onError path
      return new Promise((_, reject) =>
        reject(`Unexpected event type: ${event.type}. Expected type "LOGIN".`),
      )
    },
    [backendUrl, headers],
  )

  const obtainGuestToken: TAuthMachineService = React.useCallback(
    () => fetchTokenForGuest({ backendUrl, headers }),
    [backendUrl, headers],
  )

  return { obtainUserToken, obtainGuestToken }
}
